import { Clock } from "three";
import { createScene } from "../world/SceneSetup";
import { Arena } from "../world/Arena";
import { Input } from "../input/Input";
import { Hud } from "../ui/hud";
import { BuildMode } from "./modes/BuildMode";
import type { GameMode, ModeContext } from "./modes/types";
import { Fx } from "../fx/fx";
import { quality, setLowQuality } from "../quality";
import { sfx, unlockAudio } from "../audio/sfx";
import { Net, PUBLIC_ROOM, roomFromUrl } from "../net/Net";

type Phase = "title" | "playing" | "end";

/**
 * Kickoff: paste the brief, then change makeMode() only.
 * Everything a mode needs is on `this.ctx` (also window.__game.ctx).
 */
export class Game {
  readonly ctx: ModeContext;
  private applyQuality: () => void;
  private disposeScene: () => void;
  private mode: GameMode | null = null;
  private phase: Phase = "title";
  private clock = new Clock();
  private raf = 0;
  private t = 0;
  private skipModeThisFrame = false;

  constructor(canvas: HTMLCanvasElement) {
    const bundle = createScene(canvas);
    this.disposeScene = bundle.dispose;
    this.applyQuality = bundle.applyQuality;

    const arena = new Arena();
    bundle.scene.add(arena.group);
    if (quality.low) arena.setFloatersVisible(false);

    const input = new Input(canvas);
    const hud = new Hud();
    const fx = new Fx();
    fx.attach(bundle.scene);
    const net = new Net();

    this.ctx = {
      scene: bundle.scene,
      camera: bundle.camera,
      renderer: bundle.renderer,
      arena,
      input,
      hud,
      fx,
      net,
    };

    hud.onStart(() => void this.playPublic());
    hud.onRetry(() => this.requestStart());
    hud.onHost(() => void this.playNamed());
    hud.onShare(() => void this.shareNet());
    net.onStatus(() => this.syncNetLine());
    hud.showTitle();
    this.syncQualityHint();
    hud.setRoomCode(roomFromUrl() || PUBLIC_ROOM);
    void this.bootLobby();

    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  }

  start() {
    this.clock.start();
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      const dt = Math.min(this.clock.getDelta(), 0.05);
      this.t += dt;
      this.tick(dt);
      this.ctx.renderer.render(this.ctx.scene, this.ctx.camera);
      this.ctx.input.endFrame();
    };
    loop();
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.mode?.stop();
    this.ctx.fx.clear();
    this.ctx.input.dispose();
    this.ctx.net.destroy();
    this.disposeScene();
  }

  /** Swap this one line when the brief lands. */
  private makeMode(): GameMode {
    return new BuildMode(this.ctx);
    // return new TemplateMode(this.ctx);
  }

  private requestStart() {
    unlockAudio();
    sfx.start();
    this.ctx.fx.flash("rgba(0, 229, 255, 0.28)");
    this.startPlay();
  }

  private startPlay() {
    const { arena, hud } = this.ctx;
    this.mode?.stop();
    arena.group.visible = true;
    arena.clearPedestalForPlay();
    hud.setTouchVisible(true);
    this.mode = this.makeMode();
    this.mode.start();
    this.phase = "playing";
    this.skipModeThisFrame = true;
  }

  private tick(dt: number) {
    const { arena, camera, input, fx, hud } = this.ctx;
    arena.update(this.t);
    fx.update(dt);

    if (input.justPressed("KeyL")) this.toggleQuality();
    if (input.justPressed("KeyF")) this.toggleFullscreen();

    if (this.phase === "title" || this.phase === "end") {
      const typing = document.activeElement instanceof HTMLInputElement;
      if (!typing && (input.justPressed("Enter") || input.justPressed("Space"))) {
        if (this.phase === "end") this.requestStart();
        else void this.playPublic();
      }
      const a = this.t * 0.15;
      camera.position.set(Math.sin(a) * 16, 9 + Math.sin(this.t * 0.4) * 0.5, Math.cos(a) * 16);
      camera.lookAt(0, 2.5, 0);
    }

    if (this.phase === "playing" && this.mode) {
      if (this.skipModeThisFrame) {
        this.skipModeThisFrame = false;
      } else {
        const result = this.mode.update(dt);
        if (result) {
          this.phase = "end";
          this.mode.stop();
          arena.restorePedestal();
          hud.setTouchVisible(false);
          fx.flash("rgba(255, 176, 32, 0.35)");
          fx.shake(0.45, 0.3);
          sfx.win();
          hud.showEnd(result.score, result.message);
        }
      }
    }

    fx.applyShake(camera);
  }

  private syncNetLine() {
    const { net, hud } = this.ctx;
    const pub = net.room === PUBLIC_ROOM;
    const who = pub ? "PUBLIC" : net.room;
    if (net.status === "host" || net.status === "joined") {
      if (pub) hud.setStartLabel(net.count > 1 ? `PLAY PUBLIC · ${net.count} HERE` : "PLAY PUBLIC");
      else hud.setStartLabel(`PLAY ${who}`);
      hud.setLobby({
        state: "ready",
        title: pub ? "Public pile is open" : `Room ${who} is open`,
        line: pub
          ? `${net.count} here · tap PLAY PUBLIC · SHARE so others land in the same pile`
          : `${net.count} here · tap PLAY THIS ROOM or SHARE the name`,
      });
    } else if (net.status === "connecting") {
      hud.setLobby({
        state: "connecting",
        title: "Opening the pile",
        line: "Almost there — PLAY still works",
      });
    } else if (net.status === "error") {
      hud.setStartLabel("PLAY ANYWAY");
      hud.setLobby({
        state: "error",
        title: "Playing solo for now",
        line: "Tap PLAY PUBLIC to retry, or name a private room",
      });
    } else {
      hud.setLobby({
        state: "connecting",
        title: "Opening the public pile",
        line: "Friends can walk in anytime",
      });
    }
  }

  private async bootLobby() {
    const { net, hud } = this.ctx;
    hud.setLobby({
      state: "connecting",
      title: "Opening the public pile",
      line: "Tap PLAY PUBLIC whenever — friends join the same pile",
    });
    try {
      const custom = roomFromUrl();
      if (custom && custom !== PUBLIC_ROOM) {
        try {
          await net.joinRoom(custom);
        } catch {
          await net.hostRoom(custom);
        }
      } else {
        await net.ensurePublic();
      }
      hud.setRoomCode(net.room);
    } catch (err) {
      console.warn(err);
      hud.setLobby({
        state: "error",
        title: "Playing solo for now",
        line: "Tap PLAY PUBLIC to retry friends",
      });
    }
  }

  private async playPublic() {
    this.ctx.hud.setRoomCode(PUBLIC_ROOM);
    this.ctx.hud.setLobby({
      state: "connecting",
      title: "Opening the public pile",
      line: "Dropping you in…",
    });
    try {
      await this.ctx.net.ensurePublic();
      this.ctx.hud.setRoomCode(this.ctx.net.room);
    } catch (err) {
      console.warn(err);
    }
    this.requestStart();
  }

  private async playNamed() {
    const code = this.ctx.hud.roomCode() || PUBLIC_ROOM;
    this.ctx.hud.setLobby({
      state: "connecting",
      title: `Opening ${code}`,
      line: "If it’s empty you host it; if it’s live you join",
    });
    try {
      try {
        await this.ctx.net.joinRoom(code);
      } catch {
        await this.ctx.net.hostRoom(code);
      }
      this.ctx.hud.setRoomCode(this.ctx.net.room);
    } catch (err) {
      console.warn(err);
    }
    this.requestStart();
  }

  private async shareNet() {
    if (!this.ctx.net.connected) await this.bootLobby();
    const result = await this.ctx.net.shareInvite();
    if (result === "copied") {
      this.ctx.hud.setLobby({ line: "Link copied — they join the same room as you." });
    } else if (result === "shared") {
      this.ctx.hud.setLobby({ line: "Invite sent — they land in this pile." });
    }
    sfx.ui();
  }

  private toggleQuality() {
    setLowQuality(!quality.low);
    this.applyQuality();
    this.ctx.arena.setFloatersVisible(!quality.low);
    this.syncQualityHint();
    sfx.ui();
  }

  private syncQualityHint() {
    this.ctx.hud.setChrome({ quality: quality.low ? "LOW" : "HIGH" });
  }

  private toggleFullscreen() {
    if (!document.fullscreenElement) void document.documentElement.requestFullscreen?.();
    else void document.exitFullscreen?.();
    sfx.ui();
  }
}
